/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-08-09
 */

// Add third-party dependencies.
import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Paper,
  Alert,
} from '@mui/material';
import { Person, Lock, Home, PhotoCamera, Settings } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// Add local dependencies.
import type { RootState } from '../../../store';
import UserProfile from '../../auth/UserProfile';
import ProfileEditForm from '../../auth/ProfileEditForm';
import ChangePasswordForm from '../../auth/ChangePasswordForm';
import BioMediaManager from '../../auth/BioMediaManager';
import Header from '../../layout/Header';
import { YouTubeVideoButton } from '../../common/YouTubeVideoButton';

/**
 * Profile page modes.
 */
type ProfileMode =
  | 'view'
  | 'edit'
  | 'bio-media'
  | 'change-password'
  | 'settings';

/**
 * Main profile page component that provides a tabbed interface for user profile management.
 * Supports viewing profile information, editing profile details, and changing password.
 *
 * @returns {React.ReactElement} The profile page component
 *
 * @example
 * // Navigate to profile page
 * navigate('/profile');
 *
 * // Navigate to specific mode
 * navigate('/profile?mode=edit');
 * navigate('/profile?mode=change-password');
 */
const Profile: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState<ProfileMode>('view');
  // Where the last email change sent its verification link. Shown until
  // dismissed or verified, since the edit form closes right after saving.
  const [emailNotice, setEmailNotice] = useState('');
  const emailVerified = useSelector(
    (state: RootState) => state.auth.user?.email_verified
  );

  // Initialize mode from URL parameters
  useEffect(() => {
    const modeParam = searchParams.get('mode');
    if (
      modeParam === 'edit' ||
      modeParam === 'bio-media' ||
      modeParam === 'change-password' ||
      modeParam === 'settings'
    ) {
      setMode(modeParam as ProfileMode);
    } else {
      setMode('view');
    }
  }, [searchParams]);

  /**
   * Handles tab change and updates URL parameters.
   * @param newMode - The new profile mode
   */
  const handleModeChange = (newMode: ProfileMode) => {
    setMode(newMode);
    if (newMode === 'view') {
      // Remove mode parameter for default view
      searchParams.delete('mode');
      setSearchParams(searchParams);
    } else {
      // Set mode parameter
      setSearchParams({ mode: newMode });
    }
  };

  /**
   * Handles successful profile edit - returns to view mode.
   * @param verificationSentTo - The new email address, if it changed
   */
  const handleEditSuccess = (verificationSentTo?: string) => {
    if (verificationSentTo) setEmailNotice(verificationSentTo);
    handleModeChange('view');
  };

  /**
   * Handles edit cancellation - returns to view mode.
   */
  const handleEditCancel = () => {
    handleModeChange('view');
  };

  /**
   * Handles successful password change - returns to view mode.
   */
  const handlePasswordChangeSuccess = () => {
    handleModeChange('view');
  };

  /**
   * Handles password change cancellation - returns to view mode.
   */
  const handlePasswordChangeCancel = () => {
    handleModeChange('view');
  };

  /**
   * Gets the current tab index based on mode.
   */
  const getTabIndex = (): number => {
    switch (mode) {
      case 'edit':
        return 1;
      case 'bio-media':
        return 2;
      case 'change-password':
        return 3;
      case 'settings':
        return 4;
      default:
        return 0;
    }
  };

  /**
   * Handles tab change from Material-UI Tabs component.
   * @param event - The tab change event
   * @param newValue - The new tab index
   */
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    switch (newValue) {
      case 1:
        handleModeChange('edit');
        break;
      case 2:
        handleModeChange('bio-media');
        break;
      case 3:
        handleModeChange('change-password');
        break;
      case 4:
        // Navigate to the dedicated Settings page
        navigate('/settings');
        break;
      default:
        handleModeChange('view');
        break;
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      {/* Header */}
      <Header />

      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={e => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
            }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('navigation.home')}
          </Link>
          <Typography
            color="text.primary"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Person sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('profile.breadcrumb')}
          </Typography>
        </Breadcrumbs>

        {/* Page Header */}
        <Typography variant="h4" component="h1" gutterBottom>
          {t('profile.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('profile.description')}
        </Typography>

        {/* Tutorial Video Section */}
        <Alert
          severity="info"
          sx={{
            mb: 4,
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography variant="body2" sx={{ flex: 1 }}>
              <strong>Need help updating your profile?</strong> Watch our
              step-by-step tutorial to learn how to add photos, write a bio, and
              customize your profile settings.
            </Typography>
            <Box sx={{ flexShrink: 0 }}>
              <YouTubeVideoButton
                videoId="QYDrwRvE2bU"
                videoTitle="How to Update Your Profile on tools.dance"
                buttonText="Watch Tutorial"
                size="small"
              />
            </Box>
          </Box>
        </Alert>

        {/* Navigation Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={getTabIndex()}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Tab
              icon={<Person />}
              label={t('profile.tabs.viewProfile')}
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
            <Tab
              icon={<Person />}
              label={t('profile.tabs.editProfile')}
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
            <Tab
              icon={<PhotoCamera />}
              label={t('profile.tabs.bioMedia')}
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
            <Tab
              icon={<Lock />}
              label={t('profile.tabs.changePassword')}
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
            <Tab
              icon={<Settings />}
              label={t('profile.tabs.settings')}
              iconPosition="start"
              sx={{ minHeight: 64 }}
            />
          </Tabs>
        </Paper>

        {/* Content Area */}
        <Box sx={{ mt: 3 }}>
          {emailNotice && !emailVerified && (
            <Alert
              severity="info"
              onClose={() => setEmailNotice('')}
              sx={{ mb: 3 }}
            >
              {t('profile.emailChangeNotice', { email: emailNotice })}
            </Alert>
          )}

          {mode === 'view' && (
            <UserProfile
              onEditClick={() => handleModeChange('edit')}
              onAvatarClick={() => handleModeChange('bio-media')}
            />
          )}

          {mode === 'edit' && (
            <ProfileEditForm
              onSave={handleEditSuccess}
              onCancel={handleEditCancel}
            />
          )}

          {mode === 'bio-media' && (
            <BioMediaManager
              onSave={handleEditSuccess}
              onCancel={handleEditCancel}
            />
          )}

          {mode === 'change-password' && (
            <ChangePasswordForm
              onSuccess={handlePasswordChangeSuccess}
              onCancel={handlePasswordChangeCancel}
            />
          )}

          {mode === 'settings' && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('profile.settingsPlaceholder.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('profile.settingsPlaceholder.description')}
              </Typography>
            </Paper>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default Profile;
